import { AbstractService } from './abstract-service';
import { ServiceCallType } from './definitions';
import { ServiceCallRunner } from './service-call-runner';

/**
 * This class will be augmented when importing specific service
 * For example:
 * ```typescript
 * const container: ServiceContainer;
 * expect(container.interface).to.be.undefined;
 * import '@iotize/tap/service/interface';
 * expect(container.interface).to.be.not.undefined;
 *
 * ```
 */
export class ServiceContainer {
  constructor(public readonly serviceCallRunner: ServiceCallRunner) {}

  /**
   * List loaded services
   */
  services(): AbstractService[] {
    return Object.values(this.servicesMap());
  }

  servicesMap(): Record<string, AbstractService> {
    const services: Record<string, AbstractService> = {};
    for (const key in this) {
      const hasDescriptor = Object.getOwnPropertyDescriptor(
        ServiceContainer.prototype,
        key,
      );
      const value = this[key];
      if (hasDescriptor?.enumerable) {
        if (value instanceof AbstractService) {
          services[key] = value;
        }
      }
    }
    return services;
  }

  /**
   * List all loaded resources
   */
  resources(): ServiceCallType[] {
    return this.services().reduce(
      (prev: ServiceCallType[], service: AbstractService) => {
        prev.push(...Object.values(service.resources));
        return prev;
      },
      [],
    );
  }
}

export function extendServiceContainer<T extends AbstractService>(
  attrName: string,
  serviceConstructor: new (serviceCallRunner: ServiceCallRunner) => T,
) {
  const hiddenAttrName = `__${attrName}__`;
  Object.defineProperty(ServiceContainer.prototype, hiddenAttrName, {
    enumerable: false,
    configurable: true,
    writable: true,
    value: undefined,
  });
  return Object.defineProperty(ServiceContainer.prototype, attrName, {
    get(this: ServiceContainer) {
      const that = this as any;
      if (!that[hiddenAttrName]) {
        that[hiddenAttrName] = new serviceConstructor(this.serviceCallRunner);
      }
      return that[hiddenAttrName];
    },
    enumerable: true,
    configurable: true,
  });
}
